import { ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import type { NavItem, StammdatenNavGroup } from "@/lib/nav";
import { NavBadge } from "@/navigation/NavBadge";
import { NavIcon } from "@/navigation/NavIcon";
import { useNav } from "@/navigation/navContext";

interface StammdatenPageHeaderProps {
  item: NavItem;
  group?: StammdatenNavGroup;
}

/**
 * Einheitlicher Seitenkopf für alle Stammdaten-Bereiche: Pfad (Stammdaten › Bereich),
 * Symbol, Titel und ein Satz dazu, was hier eingestellt wird.
 */
export function StammdatenPageHeader({ item, group }: StammdatenPageHeaderProps) {
  const { badges } = useNav();

  return (
    <header className="space-y-3">
      <nav aria-label="Pfad" className="flex flex-wrap items-center gap-1 text-xs text-label-secondary">
        <Link to="/stammdaten" className="font-medium text-accent hover:underline">
          Stammdaten
        </Link>
        {group ? (
          <>
            <ChevronRight size={14} aria-hidden className="text-label-tertiary" />
            <span>{group.label}</span>
          </>
        ) : null}
        <ChevronRight size={14} aria-hidden className="text-label-tertiary" />
        <span className="text-label" aria-current="page">
          {item.label}
        </span>
      </nav>

      <div className="flex items-start gap-3">
        {item.icon ? (
          <span
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-accent-soft text-accent"
            aria-hidden
          >
            <NavIcon name={item.icon} size={22} />
          </span>
        ) : null}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-xl font-semibold tracking-tight text-label sm:text-2xl">{item.label}</h1>
            <NavBadge item={item} badges={badges} />
          </div>
          {item.description ? (
            <p className="mt-1 text-sm leading-relaxed text-label-secondary">{item.description}</p>
          ) : null}
        </div>
      </div>
    </header>
  );
}
